import {UpdateBranchButton} from '@github-ui/mergebox/components/sections/conflicts/UpdateBranchButton'
import {announce} from '@github-ui/aria-live'
import {Banner} from '@primer/react'
import {useState} from 'react'
import {type MergeabilityData, useMergeabilityData} from '../use-mergeability-data'

export type BranchUpdateRequirement = 'required' | 'optional'

export function getBranchUpdateRequirement(data: MergeabilityData | null): BranchUpdateRequirement | null {
  if (
    !data ||
    data.isClosedOrMerged ||
    // `viewerCanUpdateBranch` (PullRequest#async_branch_is_updatable_by?) requires the branch to
    // actually be behind base (PullRequest#behind_base?), a plain git ahead/behind comparison
    // independent of mergeStateStatus. It rules out an already up-to-date PR, but a behind branch
    // can still have conflicts, an advisory workspace, or be stacked/pending — the shouldRender and
    // sectionStatus checks below exclude those so this item doesn't preempt the ones that handle them.
    !data.mergeBoxPullRequest.viewerCanUpdateBranch ||
    !data.conflictsSection?.shouldRender ||
    !(data.conflictsSection?.sectionStatus === 'OUT_OF_DATE' || data.conflictsSection?.sectionStatus === 'NO_CONFLICTS')
  ) {
    return null
  }

  return data.requiresBranchUpdate ? 'required' : 'optional'
}

export function OverviewUpdateBranchControl({
  itemName,
  onError,
}: {
  itemName: string
  onError?: (message: string | null) => void
}) {
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  if (!data || !getBranchUpdateRequirement(data)) return null

  return (
    <>
      <UpdateBranchButton
        analyticsOverrides={{
          selectMergeMethod: {
            eventType: 'overview.for_you_item_action',
            target: 'OVERVIEW_FOR_YOU_ITEM',
            payload: {item_name: itemName, action_name: 'select_merge_commit_method'},
          },
          selectRebaseMethod: {
            eventType: 'overview.for_you_item_action',
            target: 'OVERVIEW_FOR_YOU_ITEM',
            payload: {item_name: itemName, action_name: 'select_rebase_method'},
          },
          updateBranch: {
            eventType: 'overview.for_you_item_action',
            target: 'OVERVIEW_FOR_YOU_ITEM',
            payload: {item_name: itemName, action_name: 'update_branch'},
          },
        }}
        headRefOid={data.mergeBoxPullRequest.headRefOid}
        onError={message => {
          if (onError) {
            onError(message)
          } else {
            setErrorMessage(message)
            if (message) announce(message, {assertive: true})
          }
        }}
        size="small"
        viewerUpdateMethods={data.mergeBoxPullRequest.viewerUpdateMethods}
      />
      {!onError && errorMessage && (
        <Banner variant="critical" title="Error" hideTitle>
          {errorMessage}
        </Banner>
      )}
    </>
  )
}
